/**
 * Device classification for the recording flow.
 *
 * Two orthogonal facts the record UI needs, kept pure and injectable so they
 * are testable without a browser:
 *
 * - **`is_ios`** — a telemetry field only (`RecordingDeviceProps`). It is
 *   deliberately NOT a control-flow input: the iOS `MediaRecorder` nudges are
 *   applied unconditionally, because gating them on this sniff would silently
 *   turn them off on iPadOS 13+, which reports a Macintosh user agent.
 * - **`device_type`** — drives the mobile-first MIME ordering (`mimeSupport.ts`)
 *   and stamps `DeviceProps` on analytics.
 *
 * Every function takes the user-agent string as an argument (defaulting to
 * `navigator.userAgent`) so the classification is a pure function under test.
 */
import type { DeviceProps, DeviceType, RecordingDeviceProps } from "@reflect/domain";

/** The UA substrings legacy sniffs for iOS (`VideoComponent.jsx`, every event's `is_ios`). */
const IOS_UA_PATTERN = /iPad|iPhone|iPod/;

/** UA substrings that mark a handheld/mobile browser for MIME ordering. */
const MOBILE_UA_PATTERN = /Android|iPad|iPhone|iPod|Mobi/i;

/** The current user agent, or an empty string in a non-browser context. */
function currentUserAgent(): string {
  return typeof navigator === "undefined" ? "" : navigator.userAgent;
}

/**
 * Whether the user agent looks like iOS. **Telemetry only** — never branch
 * capture behaviour on this: iPadOS 13+ reports a Macintosh UA, so a
 * false negative here must never disable a workaround the device needs.
 *
 * @param userAgent - the UA string to classify; defaults to the live one.
 */
export function isIos(userAgent: string = currentUserAgent()): boolean {
  return IOS_UA_PATTERN.test(userAgent);
}

/**
 * Whether the user agent looks like a mobile/handheld browser, used to order
 * the MIME candidate list MP4-first (`mimeSupport.ts`).
 *
 * @param userAgent - the UA string to classify; defaults to the live one.
 */
export function isMobile(userAgent: string = currentUserAgent()): boolean {
  return MOBILE_UA_PATTERN.test(userAgent);
}

/**
 * The coarse device class stamped on analytics and used for MIME ordering.
 *
 * @param userAgent - the UA string to classify; defaults to the live one.
 */
export function deviceType(userAgent: string = currentUserAgent()): DeviceType {
  return isMobile(userAgent) ? "mobile" : "desktop";
}

/**
 * The `DeviceProps` every recording-flow analytics event carries.
 *
 * @param userAgent - the UA string to classify; defaults to the live one.
 */
export function deviceProps(userAgent: string = currentUserAgent()): DeviceProps {
  return { device_type: deviceType(userAgent) };
}

/**
 * The `RecordingDeviceProps` recording events carry — `device_type` plus the
 * iOS telemetry flag.
 *
 * @param userAgent - the UA string to classify; defaults to the live one.
 */
export function recordingDeviceProps(userAgent: string = currentUserAgent()): RecordingDeviceProps {
  return { device_type: deviceType(userAgent), is_ios: isIos(userAgent) };
}
